(() => {
  function initialize(root = document) {
    root.querySelectorAll('[data-brand-directory]').forEach(directory => {
      const input = directory.querySelector('[data-brand-query]');
      if (!input || input.dataset.ready) return;
      input.dataset.ready = 'true';
      const cards = [...directory.querySelectorAll('[data-brand-name]')];
      const counter = directory.querySelector('[data-brand-count]');
      const originalCount = counter.textContent;
      const filter = () => {
        const query = input.value.trim().toLocaleLowerCase();
        let count = 0;
        cards.forEach(card => {
          card.hidden = !card.dataset.brandName.toLocaleLowerCase().includes(query);
          if (!card.hidden) count++;
        });
        counter.textContent = query ? `${count} / ${originalCount}` : originalCount;
        directory.querySelector('[data-brand-empty]').hidden = count > 0;
      };
      input.addEventListener('input', filter);
      directory.querySelector('[data-brand-reset]').addEventListener('click', () => {
        input.value = '';
        filter();
        input.focus();
      });
    });
    root.querySelectorAll('[data-filter-panel]').forEach(panel => { panel.hidden = true; });
  }

  document.addEventListener('click', event => {
    const toggle = event.target.closest('[data-filter-toggle]');
    if (toggle) {
      const panel = document.getElementById(toggle.getAttribute('aria-controls'));
      panel.hidden = !panel.hidden;
      toggle.setAttribute('aria-expanded', String(!panel.hidden));
    }
    document.querySelectorAll('[data-navigation][open], [data-header-menu][open]').forEach(menu => {
      if (!menu.contains(event.target)) menu.open = false;
    });
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-navigation][open], [data-header-menu][open]').forEach(menu => {
      menu.open = false;
      menu.querySelector('summary').focus();
    });
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-sort]')) event.target.form.requestSubmit();
  });
  document.addEventListener('shopify:section:load', event => initialize(event.target));

  class StoreRail extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      const track = this.querySelector('[data-rail-track]');
      const controls = this.querySelector('[data-rail-controls]');
      if (!track || !controls) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      let drag = null;
      let suppressClick = false;
      const update = () => {
        const limit = track.scrollWidth - track.clientWidth;
        controls.hidden = limit < 2;
        controls.querySelector('[data-rail-direction="-1"]').disabled = track.scrollLeft < 2;
        controls.querySelector('[data-rail-direction="1"]').disabled = track.scrollLeft >= limit - 2;
      };
      controls.addEventListener('click', event => {
        const button = event.target.closest('[data-rail-direction]');
        if (!button) return;
        track.scrollBy({ left: track.clientWidth * Number(button.dataset.railDirection), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }, options);
      track.addEventListener('scroll', update, { ...options, passive: true });
      track.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch' || event.button !== 0 || event.target.closest('button, input, select, textarea')) return;
        suppressClick = false;
        drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft, moved: false };
      }, options);
      track.addEventListener('pointermove', event => {
        if (!drag || event.pointerId !== drag.id) return;
        const distance = event.clientX - drag.x;
        if (!drag.moved && Math.abs(distance) < 6) return;
        if (!drag.moved) {
          drag.moved = true;
          track.setPointerCapture(event.pointerId);
          this.classList.add('is-dragging');
        }
        track.scrollLeft = drag.left - distance;
      }, options);
      const endDrag = event => {
        if (!drag || event.pointerId !== drag.id) return;
        if (drag.moved) {
          suppressClick = true;
          window.setTimeout(() => { suppressClick = false; }, 0);
        }
        drag = null;
        this.classList.remove('is-dragging');
      };
      track.addEventListener('pointerup', endDrag, options);
      track.addEventListener('pointercancel', endDrag, options);
      track.addEventListener('dragstart', event => event.preventDefault(), options);
      track.addEventListener('click', event => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressClick = false;
      }, { ...options, capture: true });
      this.observer?.disconnect();
      this.observer = new ResizeObserver(update);
      this.observer.observe(track);
      [...track.children].forEach(child => this.observer.observe(child));
      update();
    }

    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
  }
  if (!customElements.get('store-rail')) customElements.define('store-rail', StoreRail);

  class CollectionTabs extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      const navigation = this.querySelector('[data-collection-tabs]');
      this.tabs = [...this.querySelectorAll('[data-tab-target]')];
      if (!navigation || !this.tabs.length) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.classList.add('is-enhanced');
      navigation.hidden = false;
      navigation.setAttribute('role', 'tablist');
      this.tabs.forEach(tab => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', tab.dataset.tabTarget);
        const panel = document.getElementById(tab.dataset.tabTarget);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.tabIndex = 0;
      });
      this.select(this.tabs[0]);
      navigation.addEventListener('click', event => {
        const tab = event.target.closest('[data-tab-target]');
        if (tab) this.select(tab);
      }, options);
      navigation.addEventListener('keydown', event => {
        const index = this.tabs.indexOf(event.target);
        if (index < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
        this.select(this.tabs[next]);
        this.tabs[next].focus();
      }, options);
      this.addEventListener('shopify:block:select', event => {
        const panel = event.target.closest('[data-collection-panel]');
        const tab = this.tabs.find(item => item.dataset.tabTarget === panel?.id);
        if (tab) this.select(tab);
      }, options);
    }

    select(selected) {
      this.tabs.forEach(tab => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        document.getElementById(tab.dataset.tabTarget).hidden = !active;
      });
    }

    disconnectedCallback() { this.controller?.abort(); }
  }
  if (!customElements.get('collection-tabs')) customElements.define('collection-tabs', CollectionTabs);

  class CampaignSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.track = this.querySelector('[data-campaign-track]');
      this.slides = [...this.querySelectorAll('[data-campaign-slide]')];
      const controls = this.querySelector('[data-campaign-controls]');
      if (!controls || this.slides.length < 2) return;
      controls.hidden = false;
      this.index = 0;
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-campaign-direction]');
        if (button) this.goTo(this.index + Number(button.dataset.campaignDirection));
      });
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          const trackLeft = this.track.getBoundingClientRect().left;
          this.index = this.slides.reduce((closest, slide, index) => Math.abs(slide.getBoundingClientRect().left - trackLeft) < Math.abs(this.slides[closest].getBoundingClientRect().left - trackLeft) ? index : closest, 0);
          this.querySelector('[data-campaign-count]').textContent = `${this.index + 1} / ${this.slides.length}`;
        });
      }, { passive: true });
      this.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.goTo(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      this.editorSelect = event => {
        const index = this.slides.findIndex(slide => slide === event.target || slide.contains(event.target));
        if (index >= 0) this.goTo(index);
      };
      this.addEventListener('shopify:block:select', this.editorSelect);
    }

    disconnectedCallback() { cancelAnimationFrame(this.scrollFrame); }

    goTo(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides[this.index].scrollIntoView({ block: 'nearest', inline: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
  }
  if (!customElements.get('campaign-slideshow')) customElements.define('campaign-slideshow', CampaignSlideshow);

  class ProductDetail extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.normalizeDescription();
      this.addEventListener('click', event => this.onClick(event));
      this.addEventListener('submit', event => this.onSubmit(event));
    }

    disconnectedCallback() { this.controller?.abort(); }

    normalizeDescription() {
      // Older supplier descriptions contain page headings inside their rich text.
      this.querySelectorAll('.product-description h1').forEach(heading => {
        const replacement = document.createElement('h2');
        for (const attribute of heading.attributes) replacement.setAttribute(attribute.name, attribute.value);
        replacement.append(...heading.childNodes);
        heading.replaceWith(replacement);
      });
    }

    onClick(event) {
      const option = event.target.closest('[data-option-link]');
      if (option && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
        event.preventDefault();
        if (!this.cartPending) this.selectOption(option);
        return;
      }
      const thumbnail = event.target.closest('[data-media-target]');
      if (thumbnail) {
        event.preventDefault();
        const stage = thumbnail.closest('[data-gallery]')?.querySelector('.product-gallery__stage');
        const aspectRatio = Number(thumbnail.dataset.mediaAspectRatio);
        if (stage && Number.isFinite(aspectRatio) && aspectRatio > 0) {
          stage.style.setProperty('--active-media-aspect-ratio', aspectRatio);
        }
        this.querySelectorAll('[data-media-id]').forEach(media => {
          media.hidden = media.dataset.mediaId !== thumbnail.dataset.mediaTarget;
          if (media.hidden) {
            media.querySelectorAll('video').forEach(video => video.pause());
            media.querySelectorAll('iframe').forEach(frame => {
              // External players have different APIs; resetting their source stops playback.
              frame.src = frame.src;
            });
          }
        });
        this.querySelectorAll('[data-media-target]').forEach(item => {
          if (item === thumbnail) item.setAttribute('aria-current', 'true');
          else item.removeAttribute('aria-current');
        });
      }
      const stepper = event.target.closest('[data-quantity-step]');
      if (stepper) {
        const input = stepper.parentElement.querySelector('input');
        Number(stepper.dataset.quantityStep) > 0 ? input.stepUp() : input.stepDown();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    async selectOption(option) {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const url = new URL(option.href);
      const current = new URL(location.href);
      if (current.searchParams.has('view')) url.searchParams.set('view', current.searchParams.get('view'));
      // Merge rapid changes against pending choices, not the last rendered selection.
      if (this.pendingOptions && url.pathname === this.pendingPath) {
        this.pendingOptions[Number(option.dataset.optionPosition) - 1] = option.dataset.optionId;
        url.searchParams.set('option_values', this.pendingOptions.join(','));
      } else {
        this.pendingOptions = url.searchParams.get('option_values').split(',');
        this.pendingPath = url.pathname;
      }
      const pageUrl = new URL(url);
      url.searchParams.set('section_id', this.dataset.renderSection || this.dataset.sectionId);
      const submit = this.querySelector('[name="add"]');
      const previousQuantity = this.querySelector('[name="quantity"]').value;
      submit.disabled = true;
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Variant request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const replacement = html.querySelector('product-detail');
        if (!replacement) throw new Error('Product section missing');
        if (controller.signal.aborted) return;
        this.innerHTML = replacement.innerHTML;
        this.normalizeDescription();
        const quantity = this.querySelector('[name="quantity"]');
        quantity.value = previousQuantity;
        if (!quantity.checkValidity()) quantity.value = quantity.min || '1';
        this.pendingOptions = null;
        if (this.dataset.updateUrl !== 'false') history.replaceState({}, '', pageUrl);
        this.querySelector(`[data-option-id="${CSS.escape(option.dataset.optionId)}"]`)?.focus({ preventScroll: true });
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.pendingOptions = null;
          this.querySelector('[data-form-status]').textContent = this.dataset.error;
          submit.disabled = this.querySelector('input[name="id"]').dataset.available !== 'true';
        }
      } finally {
        if (this.controller === controller) this.removeAttribute('aria-busy');
      }
    }

    async onSubmit(event) {
      const form = event.target.closest('[data-product-form]');
      if (!form) return;
      event.preventDefault();
      if (this.cartPending || this.hasAttribute('aria-busy')) return;
      if (!form.reportValidity()) return;
      this.cartPending = true;
      const button = form.querySelector('[name="add"]');
      const status = form.querySelector('[data-form-status]');
      const link = form.querySelector('[data-cart-link]');
      const root = window.Shopify?.routes?.root || '/';
      button.disabled = true;
      status.textContent = status.dataset.adding;
      link.hidden = true;
      try {
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || status.dataset.error);
        status.textContent = status.dataset.added;
        link.hidden = false;
        fetch(`${root}cart.js`).then(response => response.json()).then(cart => {
          document.querySelectorAll('[data-cart-count]').forEach(count => { count.textContent = cart.item_count; });
        }).catch(() => {});
      } catch (error) {
        status.textContent = error.message || status.dataset.error;
      } finally {
        button.disabled = false;
        this.cartPending = false;
      }
    }
  }
  if (!customElements.get('product-detail')) customElements.define('product-detail', ProductDetail);
  initialize();
})();
